import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiFailure } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { FlapGrid, flapBoardWidth, FLAP_REVEAL_TOTAL_MS } from "../components/flap/FlapGrid";
import { FlapStrip, stripMessage } from "../components/flap/FlapStrip";
import { NotebookBoard, NOTEBOOK_REVEAL_TOTAL_MS } from "../components/notebook/NotebookBoard";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { Grid } from "../components/wordle/Grid";
import { deriveKeyStates, type GuessRow } from "../components/wordle/keystate";
import { Keyboard } from "../components/wordle/Keyboard";
import { shareText } from "../components/wordle/share";
import { REVEAL_TOTAL_MS } from "../components/wordle/Tile";
import { colorScheme, haptic } from "../telegram";
import { useGame } from "../useGame";

const WIN_LINES = ["Геній", "Чудово", "Вражає", "Файно", "Є", "Фух"];

/**
 * One screen for every wordle-mechanics game (Wordle, Словко, Mathle): grid +
 * keyboard + reveal timing + result sheet with a copyable emoji grid. The
 * game-specific bits come in as a config.
 */
export interface GridGameConfig<V> {
  game: string;
  title: string;
  /** Tiles per row (and the input length). */
  cols: number;
  /** Guess rows out of the server view. */
  rows: (view: V) => GuessRow[];
  /** Toast copy per error code (shaken codes included). */
  errorCopy: Record<string, string>;
  /** Codes that shake the active row instead of just toasting. */
  shakeCodes: string[];
  /** Message for a too-short submit. */
  tooShort: string;
  /** Accept a typed/clicked key (lowercase). */
  charOk: (key: string) => boolean;
  /** Extra physical-keyboard mapping (e.g. x→*, ’→'); return null to ignore. */
  mapKey?: (e: KeyboardEvent) => string | null;
  /** Display transform for tiles/share ("*" → "×"). */
  pretty?: (s: string) => string;
  /** Custom on-screen keyboard. */
  keyboardRows?: string[][];
  keyLabels?: Record<string, string>;
  /** Banner above the grid (mathle target). */
  aboveGrid?: (view: V) => ReactNode;
  /** Header number formatting (wordle's "1,899"). */
  formatNumber?: (n: number) => string;
  /** Share text title ("Wordle" / "Словко" / "Mathle"). */
  shareTitle: string;
  /** Lost subtitle: what the answer was. */
  lostSubtitle: (view: V) => string;
  /** px reserved above the keyboard for the height-bound grid formula. */
  reservedPx?: number;
  /** Dynamic error message (mathle's "вираз ≠ target"); wins over errorCopy. */
  dynamicError?: (code: string, view: V) => string | null;
  /** Board look: classic tiles (default), the split-flap departures board, or the dictation notebook. */
  board?: "tiles" | "flap" | "notebook";
  /** The answer once the game is over — the flap board rolls it onto its strip. */
  answer?: (view: V) => string | undefined;
  /** Letters the flap modules rattle through while typing. */
  flapAlphabet?: string;
}

export function GridGame<V>({ config, date }: { config: GridGameConfig<V>; date?: string | undefined }) {
  const c = config;
  const g = useGame<V>(c.game, c.errorCopy, date);
  const [current, setCurrent] = useState("");
  const [revealRow, setRevealRow] = useState<number | null>(null);
  const [shakeRow, setShakeRow] = useState<number | null>(null);
  const [bounceRow, setBounceRow] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  // While a guess is revealing, everything that would spoil it (key colours,
  // streak, the strip) keeps showing the world from before that guess.
  const [pendingRow, setPendingRow] = useState<number | null>(null);
  const [heldStreak, setHeldStreak] = useState<number | null>(null);
  const [wave, setWave] = useState(0);
  const [winRow, setWinRow] = useState<number | null>(null);
  const revealSeq = useRef(0);
  const flap = c.board === "flap";
  const notebook = c.board === "notebook";
  const revealMs = flap ? FLAP_REVEAL_TOTAL_MS : notebook ? NOTEBOOK_REVEAL_TOTAL_MS : REVEAL_TOTAL_MS;
  // The notebook writes rejections in red under the grid instead of a toast.
  const [rejectNote, setRejectNote] = useState<string | null>(null);
  const rejectSeq = useRef(0);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const rows = useMemo(() => (state ? c.rows(state.view) : []), [state, c]);
  const keyStates = useMemo(
    () => deriveKeyStates(pendingRow === null ? rows : rows.slice(0, pendingRow)),
    [rows, pendingRow],
  );

  const submit = useCallback(async () => {
    if (!state || finished || g.busy) return;
    const row = rows.length;
    const reject = (msg: string) => {
      setShakeRow(row);
      haptic.error();
      if (notebook) {
        const seq = ++rejectSeq.current;
        setRejectNote(msg);
        window.setTimeout(() => rejectSeq.current === seq && setRejectNote(null), 1800);
      } else {
        g.showToast(msg);
      }
      window.setTimeout(() => setShakeRow(null), 650);
    };
    if (current.length < c.cols) return reject(c.tooShort);
    try {
      const next = await api.guess<V>(c.game, current, date);
      // Mirror useGame.run but with the reveal timing: state first, sheet after the flip.
      const seq = ++revealSeq.current;
      setHeldStreak(state.me.streak);
      setPendingRow(row);
      setCurrent("");
      g.apply(next);
      setRevealRow(row);
      if (!flap && !notebook) haptic.tap(); // those boards tick once per landed mark instead
      window.setTimeout(() => {
        if (revealSeq.current !== seq) return;
        setPendingRow(null);
        setHeldStreak(null);
        if (flap && next.status === "won") {
          setWinRow(row);
          setWave((w) => w + 1);
        }
      }, revealMs);
      if (next.status !== "playing") {
        const won = next.status === "won";
        if (won && !flap) setBounceRow(row);
        window.setTimeout(() => (won ? haptic.success() : haptic.error()), revealMs);
        const linger = flap ? (won ? 1600 : 1300) : notebook ? 1500 : won ? 900 : 300;
        window.setTimeout(() => g.setSheetOpen(true), revealMs + linger);
      }
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      const dynamic = c.dynamicError?.(code, state.view) ?? null;
      if (dynamic) reject(dynamic);
      else if (c.shakeCodes.includes(code)) reject(c.errorCopy[code] ?? c.tooShort);
      else if (code === "game_over") void g.load();
      else g.showToast(`Щось зламалось (${code})`, 2200);
    }
  }, [state, finished, g, current, date, rows.length, c, flap, notebook, revealMs]);

  const onKey = useCallback(
    (key: string) => {
      if (!state || finished || g.busy) return;
      if (key === "enter") return void submit();
      if (key === "back") return setCurrent((cur) => cur.slice(0, -1));
      if (c.charOk(key) && current.length < c.cols) {
        haptic.tap();
        setCurrent((cur) => (cur.length < c.cols ? cur + key : cur));
      }
    },
    [state, finished, g.busy, current.length, submit, c],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Enter") return onKey("enter");
      if (e.key === "Backspace") return onKey("back");
      const mapped = c.mapKey?.(e);
      if (mapped) return onKey(mapped);
      if (e.key.length === 1) onKey(e.key.toLowerCase());
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onKey, c]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const attempts = rows.length;
  const won = s.status === "won";
  const pretty = c.pretty ?? ((x: string) => x);
  const displayRows = rows.map((r) => ({ word: pretty(r.word), marks: r.marks }));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText(s.number, rows, won, colorScheme() === "dark", c.shareTitle));
      setCopied(true);
      haptic.success();
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable in some WebViews
    }
  };

  const numberLabel = c.formatNumber ? c.formatNumber(s.number) : String(s.number);
  const streak = heldStreak ?? s.me.streak;

  // The strip speaks for the board: tries left, then the verdict once the reveal lands.
  const settledStatus = pendingRow === null ? s.status : "playing";
  const settledAttempts = pendingRow ?? attempts;
  const strip = stripMessage(settledStatus, settledAttempts, WIN_LINES, c.answer?.(s.view));
  const flapReserved = (c.reservedPx ?? 350) + 64;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${numberLabel}`} title={c.title} right={date ? "" : `🔥 ${streak}`} />}>
      <div className={`flex flex-1 flex-col items-center justify-center py-1 ${flap || notebook ? "gap-2 px-2" : "gap-3"}`}>
        {c.aboveGrid?.(s.view)}
        {flap ? (
          <>
            <FlapStrip text={strip.text} tone={strip.tone} maxWidth={flapBoardWidth(c.cols, flapReserved)} />
            <FlapGrid
              guesses={displayRows}
              current={pretty(current)}
              revealRow={revealRow}
              shakeRow={shakeRow}
              wave={wave}
              winRow={winRow ?? (settledStatus === "won" ? settledAttempts - 1 : null)}
              finished={finished}
              cols={c.cols}
              reservedPx={flapReserved}
              {...(c.flapAlphabet ? { alphabet: c.flapAlphabet } : {})}
              onLand={haptic.tap}
            />
          </>
        ) : notebook ? (
          <NotebookBoard
            number={s.number}
            guesses={displayRows}
            current={pretty(current)}
            revealRow={revealRow}
            shakeRow={shakeRow}
            finished={finished}
            status={settledStatus}
            attempts={settledAttempts}
            answer={c.answer?.(s.view)}
            reject={rejectNote}
            cols={c.cols}
            reservedPx={(c.reservedPx ?? 350) + 96}
            onTick={haptic.tap}
          />
        ) : (
          <Grid
            guesses={displayRows}
            current={pretty(current)}
            revealRow={revealRow}
            shakeRow={shakeRow}
            bounceRow={bounceRow}
            finished={finished}
            cols={c.cols}
            {...(c.reservedPx !== undefined ? { reservedPx: c.reservedPx } : {})}
          />
        )}
      </div>
      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <div className="mx-auto mb-[10px] w-full max-w-[520px] px-[6px]">
          <button
            type="button"
            disabled={finished || g.busy || current.length < c.cols}
            onPointerDown={(e) => {
              e.preventDefault();
              void submit();
            }}
            className={`key flex h-[50px] w-full items-center justify-center rounded-[10px] text-[16px] font-bold uppercase tracking-wide transition-all duration-100 active:scale-[0.98] active:brightness-90 ${
              flap
                ? "bg-[#dcaa35] text-[#1e1606] shadow-[inset_0_-3px_0_rgba(0,0,0,0.3)] disabled:bg-[#2b2c30] disabled:text-[#6f7178] disabled:shadow-none"
                : notebook
                  ? "nb-check"
                  : "bg-tgbutton text-tgbuttontext disabled:opacity-35"
            }`}
          >
            {g.busy ? "…" : "Перевірити"}
          </button>
        </div>
        <Keyboard
          states={keyStates}
          onKey={onKey}
          disabled={g.busy || finished}
          variant={flap ? "flap" : notebook ? "notebook" : "classic"}
          {...(c.keyboardRows ? { rows: c.keyboardRows } : {})}
          {...(c.keyLabels ? { labels: c.keyLabels } : {})}
        />
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? (WIN_LINES[attempts - 1] ?? "Є") : "Не сьогодні"}
          subtitle={won ? `${attempts}/6 · стрік ${s.me.streak} 🔥` : `${c.lostSubtitle(s.view)} · стрік згорів`}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}/6`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          <button
            type="button"
            onClick={() => void copy()}
            className="mx-auto mt-4 block rounded-xl bg-[rgba(128,128,128,0.2)] px-4 py-2 text-sm font-semibold active:opacity-70"
          >
            {copied ? "скопійовано ✓" : "копіювати сітку"}
          </button>
        </ResultSheet>
      )}
    </Shell>
  );
}
